import { z } from 'zod';

import { CLIENT_SURFACE } from '../client-surface';
import { apiEnvelope } from '../envelope';
import { aiAvailabilitySchema } from './ai-availability';

// ─── Sona AI consent ─────────────────────────────────────────────────────────
//
// The consent to talk with Sona (an AI) is one `member_consents` type. The
// document the member agrees to is versioned here: any copy change to the gate
// bumps the version, and a decision naming a stale version is rejected so a
// member is never recorded as agreeing to text they did not see. The standing
// is the highest-version row; no row means not granted.

export const SONA_CONSENT_TYPE = 'ai_sona' as const;

/** ISO-date version of the gate copy currently served. Bump on any copy change. */
export const SONA_CONSENT_DOCUMENT_VERSION = '2026-09-25' as const;

/** Any consent document version this platform has ever served (ISO date). */
export const consentDocumentVersionFormatSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'consent document version must be an ISO date');

/** Exactly the version currently served — a write naming any other is stale. */
export const sonaConsentDocumentVersionSchema = z.literal(SONA_CONSENT_DOCUMENT_VERSION);

export const AI_CONSENT_STATUS = ['granted', 'declined', 'unknown'] as const;
export const aiConsentStatusSchema = z.enum(AI_CONSENT_STATUS);
export type AiConsentStatus = z.infer<typeof aiConsentStatusSchema>;

/** Client surfaces that can record a Sona consent decision. */
// The consent capture surface is the client surface; one vocabulary.
export const AI_CONSENT_SURFACE = CLIENT_SURFACE;
export const aiConsentSurfaceSchema = z.enum(AI_CONSENT_SURFACE);
export type AiConsentSurface = z.infer<typeof aiConsentSurfaceSchema>;

/**
 * Why a decline was recorded: the member chose "Continue without Sona" at the
 * gate (`declined`), or the client fell back to the manual path after a voice
 * failure (`voice_failed`, reserved: no fallback emits it yet). A closed
 * vocabulary so the declined outcome charts choice apart from breakage; never
 * free text. Declared once here for the client, the API and the analytics
 * registry (ADR-066).
 */
export const AI_CONSENT_DECLINE_REASON = ['declined', 'voice_failed'] as const;
export const aiConsentDeclineReasonSchema = z.enum(AI_CONSENT_DECLINE_REASON);
export type AiConsentDeclineReason = z.infer<typeof aiConsentDeclineReasonSchema>;

export const aiConsentStandingSchema = z.object({
  status: aiConsentStatusSchema,
  version: z.number().int().positive().nullable(),
  documentVersion: consentDocumentVersionFormatSchema.nullable(),
  recordedAt: z.string().nullable(),
  currentDocumentVersion: consentDocumentVersionFormatSchema,
  enforced: z.boolean(),
  // The member's AI availability, served on the standing read so a client
  // deciding whether to offer Sona (the consent gate, Settings) has it before
  // Home exists. `.optional()` lets a client parse a server that predates the
  // field or a read that failed (the server then omits it); the object staying
  // non-strict is what lets an older client parse a response carrying it
  // (ADR-135). The decision write's response never carries it.
  availability: aiAvailabilitySchema.optional(),
});
export type AiConsentStanding = z.infer<typeof aiConsentStandingSchema>;
export const aiConsentStandingResponseSchema = apiEnvelope(aiConsentStandingSchema);
export type AiConsentStandingResponse = z.infer<typeof aiConsentStandingResponseSchema>;

/**
 * Client-supplied audit facts. Strict: the member id, the surface and any
 * jurisdiction evidence are derived server-side, never accepted from the body.
 */
export const aiConsentDecisionEvidenceSchema = z
  .object({
    app_version: z.string().min(1).max(64).optional(),
    locale: z.string().min(2).max(35).optional(),
    // Meaningful on a `granted: false` decision only; the server lifts it onto
    // the declined outcome and defaults a missing value to `declined`.
    reason: aiConsentDeclineReasonSchema.optional(),
  })
  .strict();

export const aiConsentDecisionRequestSchema = z
  .object({
    granted: z.boolean(),
    documentVersion: consentDocumentVersionFormatSchema,
    surface: aiConsentSurfaceSchema,
    evidence: aiConsentDecisionEvidenceSchema.optional(),
  })
  .strict();
export type AiConsentDecisionRequest = z.infer<typeof aiConsentDecisionRequestSchema>;
